import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import * as Styled from './styled';
import type { ModalProps } from './types';

export * from './types';

export const Modal: FC<ModalProps> = (props) => {
  const { open = false, position, children, onCloseClick, shrinkHeight = false, ...rest } = props;

  const [isMounted, setIsMounted] = useState(false);
  const [fullyOpen, setFullyOpen] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      setFullyOpen(false);
      setTimeout(() => {
        setFullyOpen(open);
      }, 400);
    } else {
      document.body.removeAttribute('style');
    }
    return () => {
      document.body.removeAttribute('style');
    };
  }, [open]);

  const screenCenterPosition = useMemo((): [number, number] => {
    if (typeof window === 'undefined' || window == null) return [0, 0];
    const x = window.innerWidth / 2;
    const y = window.innerHeight / 2;
    return [x, y];
  }, []);

  if (!isMounted) {
    return null;
  }

  const modalPortal = document.getElementById('modal-root');

  return createPortal(
    <Styled.Modal
      open={open}
      position={screenCenterPosition}
      // Disabled animation, could be used later if the animation can be improved
      // position={position ?? screenCenterPosition}
      {...rest}
    >
      <Styled.ModalBackground open={open} onClick={onCloseClick} className="modal-background" />
      <Styled.Wrapper
        shrinkHeight={shrinkHeight}
        className="modal-wrapper"
        open={open}
        fullyOpen={fullyOpen}
        onClick={(event) => {
          const classList = (event.target as HTMLElement)?.classList ?? [];
          const clickedOnBackground = classList.contains('modal-background');
          const clickedOnWrapper = classList.contains('modal-wrapper');
          if (clickedOnBackground || clickedOnWrapper) {
            onCloseClick?.();
          }
        }}
      >
        {!!open && children}
      </Styled.Wrapper>
    </Styled.Modal>,
    modalPortal,
  );
};
